import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { createCompany } from '../services/credits';

// Adds a new company to the catalog. URL is required and is the dedupe key:
// if the URL already matches an existing row, the server returns
// `{ existing: true, id, name }` and we surface that to the user instead of
// creating a duplicate.

function NewCompanyRequest() {
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = location.state?.from || '/home';

    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [match, setMatch] = useState(null); // { id, name } if URL matched

    const submit = async () => {
        setError(null); setMatch(null);
        if (!name.trim()) { setError('Company name is required.'); return; }
        if (!url.trim()) { setError('Company URL is required.'); return; }
        setSubmitting(true);
        try {
            const result = await createCompany({ name: name.trim(), url: url.trim() });
            if (result.existing) {
                setMatch({ id: result.id, name: result.name });
            } else {
                navigate(returnTo);
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="centered-container">
            <div className="standard-width">
                <Box sx={{ py: 3 }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>Add a company</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Companies are matched by URL. If we already track this domain
                        we'll point you at the existing record.
                    </Typography>

                    <Paper sx={{ p: 3 }}>
                        <TextField
                            fullWidth
                            label="Company name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Company URL"
                            placeholder="https://example.com"
                            value={url}
                            onChange={e => setUrl(e.target.value)}
                            sx={{ mb: 3 }}
                        />

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {match && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                We already track this URL as <strong>{match.name}</strong>.{' '}
                                <Button
                                    size="small"
                                    onClick={() => navigate(`/company/${match.id}`)}
                                >
                                    Open it
                                </Button>
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button variant="contained" onClick={submit} disabled={submitting}>
                                {submitting ? 'Submitting…' : 'Add company'}
                            </Button>
                            <Button onClick={() => navigate(returnTo)} disabled={submitting}>
                                Cancel
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </div>
        </div>
    );
}

export default NewCompanyRequest;
